import { useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { getURLParam, parsePathParts } from "../utils/navigationUtils";
import { CREATIVE_ROUTE_CONFIG } from "../config/routeConfig";

/**
 * URL state management hook
 * Single source of truth for URL parsing and navigation
 *
 * Flat tab keys: displayUnits | displayGroups | videoUnits | videoGroups
 * RESTful URLs:  /creatives/display/units?page=1
 */
export const useURLState = () => {
  const history = useHistory();
  const location = useLocation();

  const urlState = useMemo(() => {
    const pathParts = parsePathParts(location.pathname);

    // RESTful pattern: /creatives/{display|video}/{units|groups}
    const isRESTful = pathParts.length >= 3 &&
                      pathParts[0] === 'creatives' &&
                      ['display', 'video'].includes(pathParts[1]) &&
                      ['units', 'groups'].includes(pathParts[2]);

    let tab;
    let pageNumber;

    if (isRESTful) {
      const pathKey = `${pathParts[1]}-${pathParts[2]}`;
      tab = CREATIVE_ROUTE_CONFIG.pathToTab[pathKey] || 'displayUnits';
      pageNumber = parseInt(getURLParam(location.search, 'page', '1'), 10);
    } else {
      // Fallback: default tab
      tab = 'displayUnits';
      pageNumber = 1;
    }

    const returnUrl = getURLParam(location.search, 'returnUrl');

    return {
      tab,
      pageNumber,
      returnUrl,
    };
  }, [location]);

  // Navigate using flat tab key — writes RESTful URLs
  const updateURL = (newState, options = {}) => {
    const { type = 'replace' } = options;
    const flatTab = newState.tab || urlState.tab;
    const page = newState.page || urlState.pageNumber;

    const pathInfo = CREATIVE_ROUTE_CONFIG.tabToPath[flatTab];
    if (!pathInfo) {
      console.warn('Invalid tab key:', flatTab);
      return;
    }

    const baseUrl = `/creatives/${pathInfo.media}/${pathInfo.entity}`;
    const url = page > 1 ? `${baseUrl}?page=${page}` : baseUrl;

    history[type](url);
  };

  return {
    urlState,
    updateURL,
    history,
    location,
  };
};

export default useURLState;
